import { InputValidationModel } from '../../../Common/Forms/models/input-validation.model';
import { SearchContextModel } from './search-context.model';

export class BooleanSearchKeywordsModel {
    and: string[];
    or: string [];
    not: string[];
    keywordValidations: InputValidationModel[];
    searchContexts: SearchContextModel[] = [];

    constructor() {
        this.setKeywordValidations();
    }

    /**
     * Checks if there are any keywords at all in this model.
     */
    public hasAnyKeywords(): boolean {
        return this.and.length > 0 || this.or.length > 0 || this.not.length > 0;
    }

    /**
     * Checks if there are any keywords at all in this model.
     */
    public hasAndOrKeywords(): boolean {
        return this.and.length > 0 || this.or.length > 0;
    }

    /**
     * Removes all keywords.
     */
    public clearAllKeywords(): void {
        this.and = [];
        this.or = [];
        this.not = [];
    }

    /**
    * Gets all of the keywords that should be included in the search results
    */
    public get allIncludeKeywords(): string[] {
        return [
            ...this.and,
            ...this.or
        ];
    }

    /**
    * A validation requirement that all keywords must be 2 or more characters
    */
    protected get characterLimit(): InputValidationModel {
        const require: RegExp = new RegExp('.{2,}');
        const messageKey: string = 'Monitoring.keyWords.minimumKeywordCharacterLength';

        return new InputValidationModel({
            requirements: require,
            exclusions: null,
            errorKey: messageKey
        });
    }

    /**
    * A validation requirement that no keyword may
    *  end with a a boolean operator
    */
    protected get mustNotBeOperator(): InputValidationModel {
        const exclude: RegExp = new RegExp('(^|[a-z]\\s+)(and|or|not)$', 'i');
        const messageKey: string = 'Monitoring.keyWords.keywordsCannotBeBooleanOperators';

        return new InputValidationModel({
            requirements: null,
            exclusions: exclude,
            errorKey: messageKey
        });
    }

    /**
    * A validation requirement that no keyword may
    * contain the " or | characters
    */
    protected get onlyPermittedCharacters(): InputValidationModel {
        const exclude: RegExp = new RegExp('[\|]');
        const messageKey: string = 'Monitoring.keyWords.keywordsMustOnlyBePermittedCharacters';

        return new InputValidationModel({
            requirements: null,
            exclusions: exclude,
            errorKey: messageKey
        });
    }

    /**
    * A validation requirement that no keyword may
    * start with a hyphen
    */
    protected get mustNotStartWithHyphen(): InputValidationModel {
        const exclude: RegExp = new RegExp('^-');
        const messageKey: string = 'Monitoring.keyWords.keywordsMustBeginWithPermittedCharacters';

        return new InputValidationModel({
            requirements: null,
            exclusions: exclude,
            errorKey: messageKey
        });
    }

    /**
    * A validation requirement that no keyword containing a wildcard
    * may have less than 3 characters preceding the wildcard
    */
    protected get characterLimitBeforeWildcard(): InputValidationModel {
        const exclude: RegExp = new RegExp('(^[^\\*\\?]{0,2}[\\*\\?])');
        const messageKey: string = 'Monitoring.keyWords.minimumKeywordCharacterLengthBeforeWildcard';

        return new InputValidationModel({
            requirements: null,
            exclusions: exclude,
            errorKey: messageKey
        });
    }

    /**
    * A validation requirement that no keyword containing
    * a wildcard may surround the wildcard with quotes
    */
    protected get wildcardsCannotBeUsedInQuotes(): InputValidationModel {
        // eslint-disable-next-line @typescript-eslint/quotes
        const exclude: RegExp = new RegExp(`(["'][\\*\\?]["'])`);
        const messageKey: string = 'Monitoring.keyWords.wildcardsCannotBeInQuotes';

        return new InputValidationModel({
            requirements: null,
            exclusions: exclude,
            errorKey: messageKey
        });
    }

    /**
    * A validation requirement that no keyword containing
    * a wildcard may contain word breaks
    */
    protected get wildcardsCannotBeUsedWithWordBreaks(): InputValidationModel {
        // eslint-disable-next-line @typescript-eslint/quotes
        const exclude: RegExp = new RegExp(`([-\\s;@].*[\\*\\?])|([\\*\\?].*[-\\s;@])`);
        const messageKey: string = 'Monitoring.keyWords.wildcardsCannotBeUsedWithWordBreaks';

        return new InputValidationModel({
            requirements: null,
            exclusions: exclude,
            errorKey: messageKey
        });
    }

    /**
     * A validation requirement that no keyword may contain lowercase boolean operators
     */
    protected get noLowerCaseBooleanOperators(): InputValidationModel {
        const exclude: RegExp = new RegExp('(^|[a-z]\\s+)(and|or|not)$');
        const messageKey: string = 'Monitoring.keyWords.keywordsCannotBeBooleanOperators';
        return new InputValidationModel({
            requirements: null,
            exclusions: exclude,
            errorKey: messageKey
        });
    }

    /**
    * Sets the keyword validation tests for this model
    */
    protected setKeywordValidations(): void {
        this.keywordValidations = [
            this.characterLimit,
            this.mustNotBeOperator,
            this.onlyPermittedCharacters,
            this.mustNotStartWithHyphen,
            this.characterLimitBeforeWildcard,
            this.wildcardsCannotBeUsedInQuotes,
            this.wildcardsCannotBeUsedWithWordBreaks,
            this.noLowerCaseBooleanOperators
        ];
    }

    /**
     * Convert a Keygroup defined query as a boolean query string
     *
     * @param forceQuotes
     */
    public exportKeywordGroupPhrasesToString(forceQuotes: boolean = false): string {
        let queryString = '';
        let phraseAsString: string;

        phraseAsString = this.convertSearchPhraseToString(this.and, 'AND', forceQuotes);
        queryString = this.buildQueryString(phraseAsString, queryString);

        phraseAsString = this.convertSearchPhraseToString(this.or, 'OR', forceQuotes);
        queryString = this.buildQueryString(phraseAsString, queryString);

        phraseAsString = this.convertSearchPhraseToString(this.not, 'OR', forceQuotes);
        queryString = this.buildQueryString(phraseAsString, queryString, 'NOT');

        return queryString;
    }

    /**
     * Add a phrase string to an existing query with appropriate linkage between phrases
     *
     * @param phraseAsString
     * @param queryString
     * @param prefix
     */
    private buildQueryString(phraseAsString: string, queryString: string, prefix: string = ''): string {
        if (phraseAsString.length > 0) {
            if (queryString.length === 0) {
                if (prefix !== 'NOT') {
                    queryString = queryString + '(' + phraseAsString + ')';
                }
            } else {
                queryString = queryString + ' AND ';
                if (prefix !== '') {
                    queryString = queryString + prefix + ' ';
                }
                queryString = queryString + '(' + phraseAsString + ')';
            }
        }

        return queryString;
    }

    /**
     * Build a query string from the component of a keyword group phrase, using specified linkage between terms
     *
     * @param phrase
     * @param conjunction
     * @param forceQuotes
     */
    private convertSearchPhraseToString(phrase: string[], conjunction: string, forceQuotes: boolean = false): string {
        let tempString = '';
        for (let token of phrase) {
            // If certain characters are in the token, we need to ensure we enclose it in quotes.
            // Terms already starting with '"' are pre-quoted (e.g. proximity expressions like "foo bar"~3)
            // and must not be re-wrapped.
            if (!token.startsWith('"') && (forceQuotes || token.indexOf(' ') !== -1 || token.indexOf('(') !== -1 || token.indexOf(')') !== -1)) {
                token = '"' + token + '"';
            }
            if (tempString.length === 0) {
                tempString = tempString.concat(token);
            } else {
                tempString = tempString.concat(' ' + conjunction + ' ' + token);
            }
        }

        return tempString;
    }
}
